import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Package, Upload, X, Check, Loader2, Image, Tags } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* The form only offers options that are switched on, but a product saved with
   an option that was later retired keeps showing it, so editing never silently
   changes its category or type. */
const selectableOptions = (options, currentValue) => {
  const active = options.filter((o) => o.is_active);
  if (currentValue && !active.some((o) => o.value === currentValue)) {
    const retired = options.find((o) => o.value === currentValue);
    return [...active, retired || { id: currentValue, value: currentValue, label: currentValue }];
  }
  return active;
};

/* Products store the plain value ("jumbo_chalk"); the table shows the name the
   list gives it, falling back to the raw value for anything not in the list. */
const labelFor = (options, value) => {
  const match = options.find((o) => o.value === value);
  return match ? match.label : value;
};

const defaultValue = (options, fallback) => {
  const first = options.find((o) => o.is_active);
  return first ? first.value : fallback;
};

export default function ProductsManagement() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  // The price record in force for the product being edited, so the form shows
  // what customers are actually charged rather than a fixed example.
  const [activePrice, setActivePrice] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pricingProduct, setPricingProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'white',
    product_type: 'dustless',
    stock_quantity: 0,
    retail_price: 0,
    wholesale_price: 0,
    image_url: '',
    is_active: true,
    featured: true,
    sort_order: 0
  });

  useEffect(() => {
    fetchProducts();
    fetchCatalog();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    }
  };

  const fetchCatalog = async () => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const [cats, types] = await Promise.all([
        axios.get(`${API}/product-categories`, { headers }),
        axios.get(`${API}/product-types`, { headers })
      ]);
      setCategories(cats.data);
      setProductTypes(types.data);
    } catch (error) {
      toast.error('Failed to load categories and product types');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/products`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product added successfully');
      }
      setOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: defaultValue(categories, 'white'),
      product_type: defaultValue(productTypes, 'dustless'),
      stock_quantity: 0,
      retail_price: 0,
      wholesale_price: 0,
      image_url: '',
      is_active: true,
      featured: true,
      sort_order: 0
    });
    setEditingProduct(null);
    setActivePrice(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, or WEBP');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await axios.post(`${API}/upload/image`, uploadData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = `${BACKEND_URL}${response.data.image_url}`;
      setFormData({ ...formData, image_url: imageUrl });
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, image_url: '' });
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const loadActivePrice = async (productId) => {
    setActivePrice(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/products/${productId}/prices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivePrice((res.data || []).find(p => p.status === 'active') || null);
    } catch (error) {
      console.error('Failed to fetch product prices');
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    loadActivePrice(product.id);
    setFormData({
      name: product.name,
      description: product.description,
      category: product.category,
      product_type: product.product_type,
      stock_quantity: product.stock_quantity,
      retail_price: product.retail_price || 0,
      wholesale_price: product.wholesale_price || 0,
      image_url: product.image_url || '',
      is_active: product.is_active,
      featured: product.featured || false,
      sort_order: product.sort_order || 0
    });
    setImagePreview(product.image_url || null);
    setOpen(true);
  };

  return (
    <div data-testid="products-management-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Products Management</h1>
          <p className="text-slate-600 mt-1">Manage your product catalog for customer orders</p>
        </div>
        <div className="flex items-center gap-2">
        <Button
          variant="outline"
          data-testid="manage-catalog-button"
          onClick={() => setCatalogOpen(true)}
        >
          <Tags className="mr-2" size={16} />
          Categories & Types
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-product-button" onClick={resetForm}>
              <Plus className="mr-2" size={16} />
              Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  data-testid="product-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., White Dustless Chalk"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Input
                  id="description"
                  data-testid="product-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief product description"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectableOptions(categories, formData.category).map((option) => (
                        <SelectItem key={option.id} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-600">
                    Add or edit categories with the "Categories &amp; Types" button.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="product_type">Product Type *</Label>
                  <Select value={formData.product_type} onValueChange={(value) => setFormData({ ...formData, product_type: value })}>
                    <SelectTrigger data-testid="product-type-select">
                      <SelectValue placeholder="Select a product type" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectableOptions(productTypes, formData.product_type).map((option) => (
                        <SelectItem key={option.id} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-600">
                    Add or edit types with the "Categories &amp; Types" button.
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="stock_quantity">Stock Quantity *</Label>
                <Input
                  id="stock_quantity"
                  data-testid="stock-quantity-input"
                  type="number"
                  value={formData.stock_quantity}
                  onChange={(e) => setFormData({ ...formData, stock_quantity: parseInt(e.target.value) || 0 })}
                  required
                  min="0"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="retail_price">Retail Price (Tsh) *</Label>
                  <Input
                    id="retail_price"
                    data-testid="retail-price-input"
                    type="number"
                    value={formData.retail_price}
                    onChange={(e) => setFormData({ ...formData, retail_price: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="100"
                    placeholder="e.g., 28000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wholesale_price">Wholesale Price (Tsh) *</Label>
                  <Input
                    id="wholesale_price"
                    data-testid="wholesale-price-input"
                    type="number"
                    value={formData.wholesale_price}
                    onChange={(e) => setFormData({ ...formData, wholesale_price: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="100"
                    placeholder="e.g., 20000"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Product Image</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  {(imagePreview || formData.image_url) ? (
                    <div className="relative">
                      <img 
                        src={imagePreview || formData.image_url} 
                        alt="Product preview" 
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2"
                        onClick={removeImage}
                      >
                        <X size={16} />
                      </Button>
                    </div>
                  ) : (
                    <div 
                      className="flex flex-col items-center justify-center h-48 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploading ? (
                        <Loader2 className="h-12 w-12 text-gray-400 animate-spin" />
                      ) : (
                        <>
                          <Image className="h-12 w-12 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600 text-center">
                            Click to upload product image<br />
                            <span className="text-xs text-gray-400">JPEG, PNG, GIF, WEBP (max 5MB)</span>
                          </p>
                        </>
                      )}
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="image-upload-input"
                  />
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload size={16} className="mr-2" />
                    {uploading ? 'Uploading...' : 'Upload Image'}
                  </Button>
                  <span className="text-xs text-gray-500">or paste URL below</span>
                </div>
                <Input
                  id="image_url"
                  data-testid="image-url-input"
                  value={formData.image_url}
                  onChange={(e) => {
                    setFormData({ ...formData, image_url: e.target.value });
                    setImagePreview(e.target.value || null);
                  }}
                  placeholder="https://example.com/product-image.jpg"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  data-testid="is-active-switch"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Active (visible to customers)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="featured"
                  data-testid="featured-switch"
                  checked={formData.featured}
                  onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                />
                <Label htmlFor="featured">Show on the public welcome page</Label>
              </div>
              <p className="text-xs text-gray-500 -mt-2">
                Turn this off to keep the product in the customer catalogue but hide it from the welcome page.
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm font-semibold text-blue-900">Pricing</p>
                <div className="text-xs text-blue-800 mt-2 space-y-1">
                  {activePrice ? (
                    <>
                      <p>
                        Customers are charged this product's price record:{' '}
                        <span className="font-semibold">Tsh {(activePrice.retail_price || 0).toLocaleString()}</span> retail
                      </p>
                      {(activePrice.wholesale_tiers || []).length > 0 && (
                        <p>
                          Wholesale: {activePrice.wholesale_tiers
                            .map((t) => `${t.min_qty}+ → Tsh ${(t.price || 0).toLocaleString()}`)
                            .join(' · ')}
                        </p>
                      )}
                      <p className="mt-2">
                        Change it with the price tag button in the table — the old price is kept in the
                        product's price history.
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        This product has no price record yet, so the retail and wholesale figures on this
                        form are what customers are charged.
                      </p>
                      <p className="mt-2">
                        Use the price tag button in the table to set a proper price with quantity tiers.
                        Every product is priced on its own — nothing inherits another product's price.
                      </p>
                    </>
                  )}
                </div>
              </div>
              <Button data-testid="save-product-button" type="submit" className="w-full">
                {editingProduct ? 'Update Product' : 'Add Product'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Retail Price</TableHead>
              <TableHead>Wholesale Price</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-slate-500 py-12">
                  <Package size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-lg font-semibold mb-2">No products yet</p>
                  <p className="text-sm">Add your first product to start taking customer orders</p>
                </TableCell>
              </TableRow>
            ) : (
              products.map((product) => (
                <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-12 h-12 object-cover rounded" />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                          <Package size={20} className="text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{product.name}</p>
                        <p className="text-sm text-gray-600 truncate max-w-[200px]">{product.description}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{labelFor(categories, product.category)}</Badge>
                  </TableCell>
                  <TableCell className="capitalize">{labelFor(productTypes, product.product_type)}</TableCell>
                  <TableCell className="font-medium text-green-700">Tsh {(product.retail_price || 0).toLocaleString()}</TableCell>
                  <TableCell className="font-medium text-blue-700">Tsh {(product.wholesale_price || 0).toLocaleString()}</TableCell>
                  <TableCell>
                    <span className={product.stock_quantity > 50 ? 'text-green-600 font-semibold' : product.stock_quantity > 0 ? 'text-orange-600 font-semibold' : 'text-red-600 font-semibold'}>
                      {product.stock_quantity} units
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge className={product.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'} variant="outline">
                      {product.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      data-testid={`edit-product-${product.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(product)}
                    >
                      <Pencil size={16} />
                    </Button>
                    <Button
                      data-testid={`pricing-product-${product.id}`}
                      variant="ghost"
                      size="icon"
                      title="Manage pricing"
                      onClick={() => setPricingProduct(product)}
                    >
                      <Tags size={16} className="text-[#8FC043]" />
                    </Button>
                    <Button
                      data-testid={`delete-product-${product.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(product.id)}
                    >
                      <Trash2 size={16} className="text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <CatalogDialog
        open={catalogOpen}
        onClose={() => { setCatalogOpen(false); fetchCatalog(); fetchProducts(); }}
      />

      {pricingProduct && (
        <PricingDialog
          product={pricingProduct}
          onClose={() => { setPricingProduct(null); fetchProducts(); }}
        />
      )}
    </div>
  );
}

/* Categories and product types are editable registers rather than hard-coded
   lists, so a new line of chalk can be added without a deploy. */
function CatalogDialog({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="catalog-dialog">
        <DialogHeader>
          <DialogTitle>Product Categories &amp; Types</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          These two lists fill the dropdowns on the product form. Switch an entry off to keep it on
          the products that already use it while hiding it from new ones — an entry still in use
          cannot be deleted.
        </p>
        <div className="grid md:grid-cols-2 gap-6">
          <CatalogList
            title="Categories"
            endpoint={`${API}/product-categories`}
            placeholder="e.g., Coloured Chalk"
            testid="categories"
          />
          <CatalogList
            title="Product Types"
            endpoint={`${API}/product-types`}
            placeholder="e.g., Premium"
            testid="product-types"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CatalogList({ title, endpoint, placeholder, testid }) {
  const [options, setOptions] = useState([]);
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // { id, label } while a name is being changed

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = async () => {
    try {
      const res = await axios.get(endpoint, authHeaders());
      setOptions(res.data);
    } catch {
      toast.error(`Failed to load ${title.toLowerCase()}`);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    setSaving(true);
    try {
      await axios.post(endpoint, { label: newLabel.trim(), sort_order: options.length }, authHeaders());
      setNewLabel('');
      toast.success(`Added to ${title.toLowerCase()}`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add');
    } finally {
      setSaving(false);
    }
  };

  const patch = async (option, update) => {
    try {
      await axios.put(`${endpoint}/${option.id}`, update, authHeaders());
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const remove = async (option) => {
    if (!window.confirm(`Delete "${option.label}"?`)) return;
    try {
      await axios.delete(`${endpoint}/${option.id}`, authHeaders());
      toast.success('Deleted');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-3" data-testid={`catalog-list-${testid}`}>
      <h3 className="font-semibold text-slate-900">{title}</h3>
      <div className="border border-slate-200 rounded-lg divide-y">
        {options.length === 0 ? (
          <p className="text-sm text-slate-500 p-3">Nothing here yet — add the first one below.</p>
        ) : (
          options.map((option) => (
            <div key={option.id} className="flex items-center gap-2 p-2" data-testid={`catalog-row-${option.value}`}>
              {editing && editing.id === option.id ? (
                <>
                  <Input
                    value={editing.label}
                    onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                    className="h-8"
                    autoFocus
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    title="Save name"
                    onClick={() => {
                      const label = editing.label.trim();
                      setEditing(null);
                      if (label && label !== option.label) patch(option, { label });
                    }}
                  >
                    <Check size={16} className="text-green-600" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" title="Cancel" onClick={() => setEditing(null)}>
                    <X size={16} />
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${option.is_active ? '' : 'text-slate-400'}`}>
                      {option.label}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{option.value}</p>
                  </div>
                  <Switch
                    checked={option.is_active}
                    onCheckedChange={(checked) => patch(option, { is_active: checked })}
                    title={option.is_active ? 'Offered on the product form' : 'Hidden from the product form'}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    title="Rename"
                    onClick={() => setEditing({ id: option.id, label: option.label })}
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" title="Delete" onClick={() => remove(option)}>
                    <Trash2 size={14} className="text-red-600" />
                  </Button>
                </>
              )}
            </div>
          ))
        )}
      </div>
      <form onSubmit={add} className="flex items-center gap-2">
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder={placeholder}
          data-testid={`catalog-new-${testid}`}
        />
        <Button type="submit" size="sm" disabled={saving || !newLabel.trim()}>
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
        </Button>
      </form>
    </div>
  );
}

/* Versioned pricing: one active price per product; creating a new price
   disables the old one automatically and history is kept. */
function PricingDialog({ product, onClose }) {
  const [prices, setPrices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ retail_price: product.retail_price || 0, notes: '', wholesale_tiers: [] });

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchPrices = async () => {
    try {
      const res = await axios.get(`${API}/products/${product.id}/prices`, authHeaders());
      setPrices(res.data);
    } catch {
      toast.error('Failed to load price history');
    }
  };

  useEffect(() => { fetchPrices(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addTier = () => setForm((f) => ({ ...f, wholesale_tiers: [...f.wholesale_tiers, { min_qty: 0, price: 0 }] }));
  const setTier = (i, key, value) => setForm((f) => {
    const tiers = [...f.wholesale_tiers];
    tiers[i] = { ...tiers[i], [key]: Number(value) || 0 };
    return { ...f, wholesale_tiers: tiers };
  });
  const removeTier = (i) => setForm((f) => ({ ...f, wholesale_tiers: f.wholesale_tiers.filter((_, j) => j !== i) }));

  const createPrice = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/products/${product.id}/prices`, form, authHeaders());
      toast.success('New price activated — previous price disabled');
      setShowForm(false);
      setForm({ retail_price: 0, notes: '', wholesale_tiers: [] });
      fetchPrices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create price');
    } finally {
      setSaving(false);
    }
  };

  const disablePrice = async (priceId) => {
    if (!window.confirm('Disable this price? The product will use standard pricing until a new price is created.')) return;
    try {
      await axios.put(`${API}/product-prices/${priceId}/disable`, {}, authHeaders());
      toast.success('Price disabled');
      fetchPrices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable price');
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pricing-dialog">
        <DialogHeader>
          <DialogTitle>Pricing — {product.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">Orders always use the price active at the time of ordering.</p>
            <Button size="sm" onClick={() => setShowForm(!showForm)} data-testid="new-price-button">
              <Plus size={14} className="mr-1" /> New Price
            </Button>
          </div>

          {showForm && (
            <form onSubmit={createPrice} className="border rounded-md p-4 space-y-3 bg-slate-50">
              <div className="space-y-1">
                <Label>Retail Price (Tsh) *</Label>
                <Input type="number" min="1" required value={form.retail_price} data-testid="price-retail-input"
                  onChange={(e) => setForm({ ...form, retail_price: Number(e.target.value) || 0 })} />
              </div>
              <div className="space-y-1">
                <Label>Wholesale Tiers (minimum quantity → unit price)</Label>
                {form.wholesale_tiers.map((tier, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input type="number" min="1" placeholder="Min qty" value={tier.min_qty || ''}
                      onChange={(e) => setTier(i, 'min_qty', e.target.value)} required />
                    <span className="text-gray-400">→ Tsh</span>
                    <Input type="number" min="1" placeholder="Unit price" value={tier.price || ''}
                      onChange={(e) => setTier(i, 'price', e.target.value)} required />
                    <Button type="button" size="sm" variant="outline" className="text-red-600" onClick={() => removeTier(i)}>×</Button>
                  </div>
                ))}
                <Button type="button" size="sm" variant="outline" onClick={addTier}>+ Add tier</Button>
              </div>
              <div className="space-y-1">
                <Label>Notes</Label>
                <Input value={form.notes} placeholder="e.g. Aug 2026 price review"
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <Button type="submit" className="w-full" disabled={saving} data-testid="save-price-button">
                {saving ? 'Saving...' : 'Activate New Price'}
              </Button>
            </form>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Retail</TableHead>
                <TableHead>Wholesale Tiers</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {prices.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-gray-400">
                  No price records — the product uses standard built-in pricing.
                </TableCell></TableRow>
              )}
              {prices.map((rec) => (
                <TableRow key={rec.id}>
                  <TableCell className="text-xs">{(rec.created_at || '').slice(0, 10)}<br />
                    <span className="text-gray-400">{rec.created_by}</span></TableCell>
                  <TableCell>Tsh {(rec.retail_price || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-xs">
                    {(rec.wholesale_tiers || []).map((t) => `${t.min_qty}+ → ${t.price.toLocaleString()}`).join(' · ') || '—'}
                  </TableCell>
                  <TableCell>
                    <Badge className={rec.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                      {rec.status}
                    </Badge>
                    {rec.status === 'disabled' && rec.disabled_at && (
                      <p className="text-[10px] text-gray-400 mt-1">disabled {(rec.disabled_at || '').slice(0, 10)}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    {rec.status === 'active' && (
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => disablePrice(rec.id)}
                        data-testid={`disable-price-${rec.id}`}>
                        Disable
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
